/**
 * ELK layout engine integration for the journey builder.
 *
 * ⚠️  HERE BE DRAGONS ⚠️
 *
 * This file is the bridge between the Journey Builder's React Flow graph and
 * the ELK (Eclipse Layout Kernel) automatic layout algorithm. It handles:
 *
 *   - Converting React Flow nodes/edges into an ELK graph representation
 *   - Running the ELK layered layout algorithm
 *   - Applying ELK-computed positions back to React Flow nodes
 *   - Applying ELK-computed edge routing (bend points) to edges
 *   - Post-ELK symmetry corrections for branching layouts (via symmetrize-helpers)
 *   - Viewport bounds calculation for canvas sizing
 *
 * The layout pipeline is: convertToELKGraph → ELK.layout → symmetrize → apply.
 * Branching nodes (variant split / condition) use ELK ports for per-branch
 * edge routing, and a post-layout symmetrization pass to produce visually
 * balanced branch lanes.
 *
 * WHEN TO MODIFY THIS FILE:
 *   - Fixing layout bugs (nodes overlapping, edges misrouted, etc.)
 *   - Changing how the ELK graph is constructed (new node/edge types)
 *   - Adjusting the symmetrization pipeline for branching layouts
 *
 * WHEN NOT TO MODIFY THIS FILE:
 *   - Adding a new step type → update step-factory.ts and step-config.tsx
 *   - Changing graph mutation logic → update graph-mutations.ts
 *   - Changing layout constants → update layout-config.ts
 *   - Changing edge/node rendering → update the edge-types/ or node-types/ components
 */
import { ELKLayoutConfig, ELKLayoutResult, NodePosition, PortPosition, ViewportBounds, ElkNode, ElkExtendedEdge, ElkEdgeSection } from './elk-types'
import { JourneyNode, JourneyEdge, BranchEdgeData, JourneyStepData } from '../types/journey-nodes'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { LAYOUT_CONSTANTS } from './elk-config'
import { isBranchingNode, getBranchingNodeData } from '../utils/graph/traversal'
import { getNodeOutStepId } from '../utils/graph/traversal'
import {
    buildSplitContext,
    computeBranchWidths,
    computeLaneCenters,
    shiftBranchesToLanes,
    verticallyCenterBranches,
    enforceBranchesBelowSplit,
    repositionJoinTarget,
    cascadeDownstreamYDelta,
    clearSplitRegionEdgeRouting,
    updatePortPositions,
    computeConvergeContext,
    generateBranchEdgeSections,
    generateConvergenceEdgeSections,
    SplitContext,
} from './elk-symmetrize'
import ELK from 'elkjs/lib/elk.bundled.js'

const elk = new ELK()

/**
 * Sort nodes into variant-aware traversal order for ELK model ordering.
 *
 * ELK's forceNodeModelOrder places nodes left-to-right based on their
 * position in the children array. Without sorting, the exit node can
 * appear before variant path nodes, causing ELK to flip port order
 * to minimize crossings.
 *
 * Order: trigger → split → variant A path → variant B path → join/exit
 */
function sortNodesForModelOrder(nodes: JourneyNode[], edges: JourneyEdge[]): JourneyNode[] {
    const nodeMap = new Map(nodes.map(n => [n.id, n]))

    const ordered: JourneyNode[] = []
    const visited = new Set<string>()

    function visit(nodeId: string) {
        if (visited.has(nodeId)) return
        const node = nodeMap.get(nodeId)
        if (!node) return

        visited.add(nodeId)
        ordered.push(node)

        if (isBranchingNode(node)) {
            const branchingData = getBranchingNodeData(node)
            if (!branchingData) return
            const joinStepId = branchingData.join_step_id ? String(branchingData.join_step_id) : null

            // Walk each branch path in order, recursing into nested branching nodes
            for (const branch of branchingData.branches) {
                if (!branch.out_step_id) continue
                const outId = String(branch.out_step_id)
                if (outId === joinStepId) continue

                let currentId: string | null = outId
                while (currentId && currentId !== joinStepId) {
                    if (visited.has(currentId)) break
                    const currentNode = nodeMap.get(currentId)
                    if (!currentNode) break

                    // Recurse — handles nested branching nodes at any depth
                    visit(currentId)

                    // After visit, advance past the node. For branching nodes,
                    // visit() already walked all branches, so skip to join target.
                    if (isBranchingNode(currentNode)) {
                        const nestedData = getBranchingNodeData(currentNode)
                        currentId = nestedData?.join_step_id ? String(nestedData.join_step_id) : null
                    } else {
                        currentId = getNodeOutStepId(currentNode)
                    }
                }
            }

            // Visit join target last
            if (joinStepId) {
                visit(joinStepId)
            }
        } else {
            const nextId = getNodeOutStepId(node)
            if (nextId) {
                visit(nextId)
            }
        }
    }

    // Start from hidden nodes (global trigger)
    for (const node of nodes) {
        if (node.hidden) visit(node.id)
    }
    // Then trigger nodes
    for (const node of nodes) {
        if (node.data.step.type === StepType.TRIGGER) visit(node.id)
    }
    // Append any unvisited nodes
    for (const node of nodes) {
        if (!visited.has(node.id)) ordered.push(node)
    }

    return ordered
}

/**
 * Convert Journey nodes and edges to ELK graph format
 */
export function convertToELKGraph(
    nodes: JourneyNode[],
    edges: JourneyEdge[],
    config: ELKLayoutConfig,
): ElkNode {
    const sortedNodes = sortNodesForModelOrder(nodes, edges)

    return {
        id: 'journey-root',
        layoutOptions: {
            'elk.algorithm': config.algorithm,
            'elk.direction': config.direction,
            'elk.edgeRouting': config.edgeRouting,
            'elk.spacing.nodeNode': `${config.nodeSeparation}`,
            'elk.layered.spacing.nodeNodeBetweenLayers': `${config.rankSeparation}`,
            'elk.spacing.edgeEdge': '40',
            'elk.layered.spacing.edgeEdgeBetweenLayers': '40',
            'elk.spacing.edgeNode': '30',
            'elk.layered.spacing.edgeNodeBetweenLayers': '30',
            'elk.layered.mergeEdges': 'false',
            'elk.layered.unnecessaryBendpoints': 'true',
            'elk.padding': `[top=${config.padding},left=${config.padding},bottom=${config.padding},right=${config.padding}]`,
            'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
            'elk.alignment': 'CENTER',
            'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
            'elk.layered.crossingMinimization.forceNodeModelOrder': 'true',
        },
        children: sortedNodes.map(node => {
            const width = node.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
            const height = node.measured?.height ?? LAYOUT_CONSTANTS.MIN_NODE_HEIGHT

            const elkNode: ElkNode = {
                id: node.id,
                width,
                height,
            }

            if (isBranchingNode(node)) {
                const branchingData = getBranchingNodeData(node)
                if (branchingData) {
                    const lastIdx = branchingData.branches.length - 1
                    elkNode.ports = branchingData.branches.map((branch, idx) => ({
                        id: `branch-${branch.key}`,
                        layoutOptions: {
                            'org.eclipse.elk.port.side': 'SOUTH',
                            // ELK orders SOUTH ports right-to-left for index 0→N,
                            // so we reverse the index to get left-to-right branch order.
                            'org.eclipse.elk.port.index': `${lastIdx - idx}`,
                        },
                    }))
                    elkNode.layoutOptions = {
                        'org.eclipse.elk.portConstraints': 'FIXED_ORDER',
                    }
                }
            }

            return elkNode
        }),
        edges: edges.map(edge => ({
            id: edge.id,
            sources: [edge.sourceHandle ? edge.sourceHandle : edge.source],
            targets: [edge.target],
        })) as ElkExtendedEdge[],
    }
}

/**
 * For purely linear graphs (no branching), align all visible nodes to a
 * common X centerline so the chain renders as a straight vertical line.
 *
 * ELK's layered algorithm can produce slight X offsets between layers
 * even when every layer contains a single node, because each layer's
 * bounding box is computed independently.
 */
function alignLinearGraph(
    nodes: JourneyNode[],
    positions: Map<string, NodePosition>,
    edgeRouting: Map<string, ElkEdgeSection[]>,
): void {
    const visibleNodes = nodes.filter(n => !n.hidden)

    // If any node is a branching node, skip — symmetrizeSplitLayout handles those.
    if (visibleNodes.some(n => isBranchingNode(n))) return

    // Find the centerline X of the first visible node (trigger) as anchor.
    let anchorCenterX: number | null = null
    for (const node of visibleNodes) {
        const pos = positions.get(node.id)
        if (pos) {
            const width = node.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
            anchorCenterX = pos.x + width / 2
            break
        }
    }

    if (anchorCenterX === null) return

    // Shift every node so its center aligns with the anchor centerline.
    for (const node of visibleNodes) {
        const pos = positions.get(node.id)
        if (!pos) continue
        const width = node.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
        pos.x = anchorCenterX - width / 2
    }

    // Clear ELK edge routing so edges fall back to React Flow's
    // getSmoothStepPath, which uses the corrected node positions.
    // For a straight vertical chain this produces clean vertical edges.
    edgeRouting.clear()
}

/**
 * Execute ELK layout and return positioned nodes with edge routing
 */
export async function calculateELKLayout(
    nodes: JourneyNode[],
    edges: JourneyEdge[],
    config: ELKLayoutConfig,
): Promise<ELKLayoutResult> {
    const elkGraph = convertToELKGraph(nodes, edges, config)

    const layoutedGraph = await elk.layout(elkGraph)

    const positions = new Map<string, NodePosition>()
    for (const node of layoutedGraph.children ?? []) {
        if (node.x !== undefined && node.y !== undefined) {
            positions.set(node.id, { x: node.x, y: node.y })
        }
    }

    const edgeRouting = new Map<string, ElkEdgeSection[]>()
    for (const edge of layoutedGraph.edges ?? []) {
        if (edge.sections && edge.sections.length > 0) {
            edgeRouting.set(edge.id, edge.sections as ElkEdgeSection[])
        }
    }

    // Extract ELK-computed port positions for split step nodes.
    // ELK's crossing minimization may reorder ports — we read the actual
    // positions so React Flow handles can match exactly.
    const portPositions = new Map<string, PortPosition[]>()
    for (const node of layoutedGraph.children ?? []) {
        if (node.ports && node.ports.length > 0) {
            portPositions.set(node.id, node.ports.map(p => ({
                portId: p.id,
                x: p.x ?? 0,
                y: p.y ?? 0,
            })))
        }
    }


    // Post-process: override ELK's split section with symmetric layout
    symmetrizeSplitLayout(nodes, edges, positions, edgeRouting, portPositions)

    // Post-process: align all nodes to the same X centerline when the graph
    // is purely linear (no branching nodes). ELK's layered algorithm can
    // produce slight X offsets between layers even for a straight chain.
    alignLinearGraph(nodes, positions, edgeRouting)

    return { positions, edgeRouting, portPositions }
}

/**
 * Post-process ELK layout to produce symmetric variant paths.
 *
 * Strategy (from the "centerline mirror + rank collision sweep" approach):
 *   1. Keep ELK's Y positions (ranks) — they're correct
 *   2. Adjust X only: mirror branch bounding boxes around the split centerline
 *   3. Center the join target on the split centerline
/**
 * Post-process ELK layout to produce symmetric variant paths.
 *
 * Strategy:
 *   1. Keep ELK's Y positions (ranks) — they're correct
 *   2. Adjust X only: mirror branch bounding boxes around the split centerline
 *   3. Center the join target on the split centerline
 *   4. Generate proper orthogonal edge sections for split edges
 *   5. Let React Flow's getSmoothStepPath handle convergence edges (custom-edge type)
 *
 * Processing order:
 *   Pass 1 (positioning): deepest-first (descending Y). Inner branching nodes
 *     are positioned first, establishing their bounding boxes. Outer branching
 *     nodes then shift those already-positioned subtrees as rigid blocks via
 *     delta-based shifting, preserving internal layout.
 *   Pass 2 (edge routing): shallowest-first (ascending Y). Outer edges are
 *     routed first so inner edge routing can reference stable outer positions.
 */
function symmetrizeSplitLayout(
    nodes: JourneyNode[],
    edges: JourneyEdge[],
    positions: Map<string, NodePosition>,
    edgeRouting: Map<string, ElkEdgeSection[]>,
    portPositions: Map<string, PortPosition[]>,
): void {
    const splitNodes = nodes.filter(n => isBranchingNode(n))

    // Pass 1: Positioning — deepest-first (descending Y).
    // Inner branching nodes get positioned first so their bounding boxes are
    // established. Outer nodes then shift those subtrees as rigid blocks.
    const splitNodesByDepthDesc = [...splitNodes].sort((a, b) => {
        const posA = positions.get(a.id)
        const posB = positions.get(b.id)
        return (posB?.y ?? 0) - (posA?.y ?? 0)
    })

    for (const splitNode of splitNodesByDepthDesc) {
        const ctx = buildSplitContext(splitNode, nodes, positions)
        if (!ctx) continue
        symmetrizeSingleBranchPositioning(ctx, edges, positions, edgeRouting)
    }

    // After all positioning: center each branching node's join target on the
    // trigger node's horizontal center.
    centerJoinTargetsOnGraph(nodes, splitNodes, positions)

    // After centering join targets, regenerate edge routing for all nodes
    // that were repositioned by centerJoinTargetsOnGraph. This includes the
    // join target itself AND every downstream node (e.g. join → trending item → exit).
    // Without this, edges between downstream nodes retain stale ELK coordinates
    // from before the centering pass, causing misaligned connections.
    const nodeMap = new Map(nodes.map(n => [n.id, n]))
    const centeredNodeIds = new Set<string>()
    for (const splitNode of splitNodes) {
        // Only top-level splits have their join targets centered — mirror the
        // same nested-check that centerJoinTargetsOnGraph uses.
        const branchingData = getBranchingNodeData(splitNode)
        if (!branchingData) continue
        const joinId = branchingData.join_step_id ? String(branchingData.join_step_id) : null
        if (!joinId) continue

        // Check if this split node is nested inside another split's scope.
        // If so, its join target was NOT centered by centerJoinTargetsOnGraph.
        let isNested = false
        for (const otherSplit of splitNodes) {
            if (otherSplit.id === splitNode.id) continue
            const otherData = getBranchingNodeData(otherSplit)
            if (!otherData) continue
            const otherJoinId = otherData.join_step_id ? String(otherData.join_step_id) : null
            for (const branch of otherData.branches) {
                const outId = branch.out_step_id ? String(branch.out_step_id) : null
                if (!outId || outId === otherJoinId) continue
                let walkId: string | null = outId
                while (walkId && walkId !== otherJoinId) {
                    if (walkId === splitNode.id) { isNested = true; break }
                    const walkNode = nodeMap.get(walkId)
                    if (!walkNode) break
                    if (isBranchingNode(walkNode)) {
                        const nestedData = getBranchingNodeData(walkNode)
                        if (nestedData?.join_step_id != null) { walkId = String(nestedData.join_step_id); continue }
                        break
                    }
                    const nextId = getNodeOutStepId(walkNode)
                    walkId = nextId
                }
                if (isNested) break
            }
            if (isNested) break
        }
        if (isNested) continue

        // Walk from join target downstream, collecting all centered node IDs.
        centeredNodeIds.add(joinId)
        let currentId: string | null = joinId
        const visited = new Set<string>([joinId])
        while (currentId) {
            const currentNode = nodeMap.get(currentId)
            if (!currentNode) break
            const nextStepId = getNodeOutStepId(currentNode)
            if (!nextStepId) break
            if (visited.has(nextStepId)) break
            visited.add(nextStepId)
            centeredNodeIds.add(nextStepId)
            currentId = nextStepId
        }
    }

    // Regenerate straight-line edge routing for any edge where the source
    // is a centered node and the target is outside the split region (not
    // handled by Pass 2's convergence edge generation).
    for (const edge of edges) {
        if (centeredNodeIds.has(edge.source)) {
            edgeRouting.delete(edge.id)

            const srcNode = nodeMap.get(edge.source)
            const srcPos = positions.get(edge.source)
            const tgtPos = positions.get(edge.target)
            if (srcPos && tgtPos) {
                const srcW = srcNode?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
                const srcH = srcNode?.measured?.height ?? LAYOUT_CONSTANTS.MIN_NODE_HEIGHT
                const tgtNode = nodeMap.get(edge.target)
                const tgtW = tgtNode?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
                const section: ElkEdgeSection = {
                    id: `${edge.id}_s0`,
                    startPoint: { x: srcPos.x + srcW / 2, y: srcPos.y + srcH },
                    endPoint: { x: tgtPos.x + tgtW / 2, y: tgtPos.y },
                }
                edgeRouting.set(edge.id, [section])
            }
        }
    }

    // Pass 2: Edge routing — shallowest-first (ascending Y).
    // Rebuild contexts so splitPos reflects post-positioning coordinates.
    const splitNodesByDepthAsc = [...splitNodes].sort((a, b) => {
        const posA = positions.get(a.id)
        const posB = positions.get(b.id)
        return (posA?.y ?? 0) - (posB?.y ?? 0)
    })

    for (const splitNode of splitNodesByDepthAsc) {
        const ctx = buildSplitContext(splitNode, nodes, positions)
        if (!ctx) continue
        symmetrizeSingleBranchEdgeRouting(ctx, edges, positions, edgeRouting, portPositions)
    }
}

/**
 * Center only top-level (outermost) branching nodes' join targets on the
 * trigger node's horizontal center. Inner branching nodes' join targets
 * are left at their content-midpoint position (set by repositionJoinTarget)
 * so their convergence edges don't cross into sibling branches' space.
 *
 * A branching node is "top-level" if it is not contained within any other
 * branching node's scope.
 *
 * Only adjusts X of join targets — Y is already correct.
 */
function centerJoinTargetsOnGraph(
    nodes: JourneyNode[],
    splitNodes: JourneyNode[],
    positions: Map<string, NodePosition>,
): void {
    // Find the trigger node's center X as the stable reference.
    const triggerNode = nodes.find(n => n.data.step.type === StepType.TRIGGER)
    if (!triggerNode) return
    const triggerPos = positions.get(triggerNode.id)
    if (!triggerPos) return
    const triggerWidth = triggerNode.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
    const triggerCenterX = triggerPos.x + triggerWidth / 2

    // Build a set of all node IDs that are inside some branching node's scope.
    const nestedNodeIds = new Set<string>()
    const nodeMap = new Map(nodes.map(n => [n.id, n]))
    for (const splitNode of splitNodes) {
        const branchingData = getBranchingNodeData(splitNode)
        if (!branchingData) continue
        const joinStepId = branchingData.join_step_id ? String(branchingData.join_step_id) : null
        for (const branch of branchingData.branches) {
            const outId = branch.out_step_id ? String(branch.out_step_id) : null
            if (!outId || outId === joinStepId) continue
            let currentId: string | null = outId
            while (currentId && currentId !== joinStepId) {
                nestedNodeIds.add(currentId)
                const currentNode = nodeMap.get(currentId)
                if (!currentNode) break
                if (isBranchingNode(currentNode)) {
                    const nestedData = getBranchingNodeData(currentNode)
                    if (nestedData?.join_step_id != null) {
                        currentId = String(nestedData.join_step_id)
                        continue
                    }
                    break
                }
                currentId = getNodeOutStepId(currentNode)
            }
        }
    }

    // Only center join targets of top-level branching nodes,
    // then cascade the centering to all downstream nodes (e.g. exit).
    for (const splitNode of splitNodes) {
        if (nestedNodeIds.has(splitNode.id)) continue

        const branchingData = getBranchingNodeData(splitNode)
        if (!branchingData) continue
        const joinStepId = branchingData.join_step_id ? String(branchingData.join_step_id) : null
        if (!joinStepId) continue

        const joinNode = nodeMap.get(joinStepId)
        const joinWidth = joinNode?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
        const joinPos = positions.get(joinStepId)
        if (!joinPos) continue

        positions.set(joinStepId, { x: triggerCenterX - joinWidth / 2, y: joinPos.y })

        // Walk downstream from the join target and center each node on the trigger centerline.
        let currentId: string | null = joinStepId
        const centered = new Set<string>([joinStepId])
        while (currentId) {
            const currentNode = nodeMap.get(currentId)
            if (!currentNode) break
            const nextId = getNodeOutStepId(currentNode)
            if (!nextId) break
            if (centered.has(nextId)) break
            centered.add(nextId)
            const nextNode = nodeMap.get(nextId)
            const nextWidth = nextNode?.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
            const nextPos = positions.get(nextId)
            if (!nextPos) break
            positions.set(nextId, { x: triggerCenterX - nextWidth / 2, y: nextPos.y })
            currentId = nextId
        }
    }
}

/**
 * Pass 1: Position nodes for a single branching step.
 * Shifts branch nodes to lanes, vertically centers branches,
 * repositions join target, and cascades Y deltas downstream.
 * Does NOT generate edge routing — that happens in pass 2.
 */
function symmetrizeSingleBranchPositioning(
    ctx: SplitContext,
    edges: JourneyEdge[],
    positions: Map<string, NodePosition>,
    edgeRouting: Map<string, ElkEdgeSection[]>,
): void {
    const { splitNode, splitPos, splitWidth, splitHeight, splitCenterX, joinStepId, branches, nodeMap } = ctx

    const branchHalfWidths = computeBranchWidths(branches, nodeMap, positions)

    const anyBranchHasNodes = branches.some(b => b.scopeNodeIds.length > 0)
    const laneCenters = computeLaneCenters(branches.length, splitCenterX, splitWidth, anyBranchHasNodes, branchHalfWidths)

    shiftBranchesToLanes(branches, laneCenters, nodeMap, positions)
    verticallyCenterBranches(branches, nodeMap, positions)
    enforceBranchesBelowSplit(branches, positions, splitPos, splitHeight)

    const { yDelta } = repositionJoinTarget(branches, nodeMap, positions, joinStepId, splitCenterX, splitPos, splitHeight)

    if (yDelta > 0) {
        cascadeDownstreamYDelta(yDelta, joinStepId, splitNode, branches, nodeMap, positions, edges, edgeRouting)
    }
}

/**
 * Pass 2: Generate edge routing for a single branching step.
 * Clears stale ELK routing, updates port positions, and generates
 * split + convergence edge sections using finalized positions.
 */
function symmetrizeSingleBranchEdgeRouting(
    ctx: SplitContext,
    edges: JourneyEdge[],
    positions: Map<string, NodePosition>,
    edgeRouting: Map<string, ElkEdgeSection[]>,
    portPositions: Map<string, PortPosition[]>,
): void {
    const { splitNode, splitPos, splitWidth, splitHeight, splitCenterX, joinStepId, branches, nodeMap, variantShaped } = ctx

    const branchHalfWidths = computeBranchWidths(branches, nodeMap, positions)
    const anyBranchHasNodes = branches.some(b => b.scopeNodeIds.length > 0)
    const laneCenters = computeLaneCenters(branches.length, splitCenterX, splitWidth, anyBranchHasNodes, branchHalfWidths)

    clearSplitRegionEdgeRouting(splitNode.id, joinStepId, branches, edges, edgeRouting)

    const ports = updatePortPositions(variantShaped, laneCenters, splitPos, splitWidth, splitHeight)
    portPositions.set(splitNode.id, ports)

    const convergeCtx = computeConvergeContext(splitPos, splitHeight, branches, positions, nodeMap, joinStepId)

    generateBranchEdgeSections(branches, ports, splitNode, edges, positions, nodeMap, laneCenters, splitPos, convergeCtx, edgeRouting)
    generateConvergenceEdgeSections(branches, edges, positions, nodeMap, convergeCtx, joinStepId, edgeRouting)
}

/**
 * Apply ELK-computed positions to Journey nodes.
 * For split step nodes, also attaches ELK port positions to uiMeta
 * so React Flow handles can be positioned to match ELK's layout.
 */
export function applyELKLayout(
    nodes: JourneyNode[],
    positions: Map<string, NodePosition>,
    portPositions: Map<string, PortPosition[]>,
): JourneyNode[] {
    let minY = Infinity
    positions.forEach(pos => {
        minY = Math.min(minY, pos.y)
    })
    const yOffset = minY === Infinity ? 0 : minY

    return nodes.map(node => {
        const position = positions.get(node.id)
        if (!position) return node

        let updatedNode: JourneyNode = {
            ...node,
            position: {
                x: position.x,
                y: position.y - yOffset,
            },
        }

        // Attach ELK port positions to split step nodes so handles match ELK layout.
        // Construct a new JourneyStepData instead of mutating via setUIMeta().
        const ports = portPositions.get(node.id)
        if (ports && ports.length > 0) {
            const nodeWidth = node.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
            updatedNode = {
                ...updatedNode,
                data: new JourneyStepData({
                    step: node.data.step,
                    uiMeta: {
                        ...node.data.uiMeta,
                        elkPortPositions: ports.map(p => ({
                            portId: p.portId,
                            leftPercent: (p.x / nodeWidth) * 100,
                        })),
                    },
                }),
            }
        }

        return updatedNode
    })
}

/**
 * Apply ELK-computed edge routing to edges that have synthetic sections.
 * This includes split edges (split → variant target) and convergence edges
 * (last variant node → join target).
 */
export function applyELKEdgeRouting(
    edges: JourneyEdge[],
    edgeRouting: Map<string, ElkEdgeSection[]>,
    positions: Map<string, NodePosition>,
): JourneyEdge[] {
    let minY = Infinity
    positions.forEach(pos => {
        minY = Math.min(minY, pos.y)
    })
    const yOffset = minY === Infinity ? 0 : minY

    return edges.map(edge => {
        const sections = edgeRouting.get(edge.id)
        if (!sections || sections.length === 0) return edge

        const normalizedSections = sections.map(section => ({
            ...section,
            startPoint: { x: section.startPoint.x, y: section.startPoint.y - yOffset },
            endPoint: { x: section.endPoint.x, y: section.endPoint.y - yOffset },
            bendPoints: section.bendPoints?.map(bp => ({ x: bp.x, y: bp.y - yOffset })),
        }))

        return {
            ...edge,
            data: {
                ...(edge.data as BranchEdgeData),
                elkSections: normalizedSections,
            },
        }
    })
}

/**
 * Extract edge routing information from ELK layout
 */
export function getELKEdgeRouting(
    layoutedGraph: ElkNode,
    edgeId: string,
) {
    const edge = layoutedGraph.edges?.find(e => e.id === edgeId)
    return edge?.sections
}

/**
 * Calculate viewport bounds from positioned nodes
 */
export function calculateViewportBounds(
    nodes: JourneyNode[],
    padding: number = 40,
): ViewportBounds {
    if (nodes.length === 0) {
        return {
            width: Math.max(padding * 2, LAYOUT_CONSTANTS.MIN_VIEWPORT_WIDTH),
            height: Math.max(padding * 2, LAYOUT_CONSTANTS.MIN_VIEWPORT_HEIGHT),
            minX: 0, maxX: 0, minY: 0, maxY: 0,
        }
    }

    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity

    for (const node of nodes) {
        const x = node.position.x
        const y = node.position.y
        const width = node.measured?.width ?? 200
        const height = node.measured?.height ?? 60

        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x + width)
        minY = Math.min(minY, y)
        maxY = Math.max(maxY, y + height)
    }

    const contentWidth = maxX - minX + padding * 2
    const contentHeight = maxY - minY + padding * 2
    const width = Math.max(contentWidth, LAYOUT_CONSTANTS.MIN_VIEWPORT_WIDTH)
    const height = Math.max(contentHeight, LAYOUT_CONSTANTS.MIN_VIEWPORT_HEIGHT)

    return {
        width, height,
        minX: minX - padding, maxX: maxX + padding,
        minY: minY - padding, maxY: maxY + padding,
    }
}